import dbCodes from '../../server/dbCodes.js'
import {getColliTypeName} from '../../utils/getColliTypeName.js'
import t from 'src/helpers/Languages.js'

// Statuses of a handled collo, per address type
export const colloStatuses = {
    delivery: {ok: 'delivered', notOk: 'notdelivered'},
    pickup: {ok: 'collected', notOk: 'notcollected'}
}

function deliveryColloOptions({collo, scanRequired, scanned, reasons, specifications}) {
    const options = []

    if (reasons.includes('missing')) {
        const specification = specifications.findIndex((specification) => specification.reason === 'missing')
        options.push({title: dbCodes.colloStatus['missing'], value: 'missing', subvalue: '', specifications: specification < -1, selected: collo.status === 'missing'})
    }

    if (collo.barcode && scanRequired) {
        const disabled = !scanned.includes(collo.barcode)
        if (reasons.includes('refused')) {
            const specification = specifications.findIndex((specification) => specification.reason === 'refused')
            options.push({title: `${dbCodes.colloStatus['refused']} (${t('scanRequired')})`, value: 'refused', subvalue: '', specifications: specification > -1, disabled, selected: collo.status === 'refused'})
        }
        options.push({title: `${dbCodes.colloStatus['delivered']} (${t('scanRequired')})`, value: 'delivered', subvalue: '', disabled, selected: collo.status === 'delivered'})
    } else {
        if (reasons.includes('refused')) {
            const specification = specifications.findIndex((specification) => specification.reason === 'refused')
            options.push({title: dbCodes.colloStatus['refused'], value: 'refused', subvalue: '', specifications: specification > -1, selected: collo.status === 'refused'})
        }
        options.push({title: dbCodes.colloStatus['delivered'], value: 'delivered', subvalue: '', selected: collo.status === 'delivered'})
    }

    return options
}

function pickupColloOptions({collo, scanRequired, scanned, reasons, specifications}) {
    const options = []

    if (reasons.includes('notready')) {
        const specification = specifications.findIndex((specification) => specification.reason === 'notready')
        options.push({title: dbCodes.colloReasons['notready'], value: 'notcollected', subvalue: 'notready', specifications: specification > -1, selected: collo.reason === 'notready'})
    }

    if (reasons.includes('invalid')) {
        const specification = specifications.findIndex((specification) => specification.reason === 'invalid')
        options.push({title: dbCodes.colloReasons['invalid'], value: 'notcollected', subvalue: 'invalid', specifications: specification > -1, selected: collo.reason === 'invalid'})
    }

    if (collo.barcode && scanRequired) {
        const disabled = !scanned.includes(collo.barcode)
        options.push({title: `${dbCodes.colloStatus['collected']} (${t('scanRequired')})`, value: 'collected', subvalue: '', disabled, selected: collo.status === 'collected'})
    } else {
        options.push({title: dbCodes.colloStatus['collected'], value: 'collected', subvalue: '', selected: collo.status === 'collected'})
    }

    return options
}

// Lets the messenger choose the status of a collo. Updates collo, colli (a new collo can be removed) and scanned in
// place and returns whether a status was chosen.
export async function chooseColloStatus(popup, {type, collo, colli, scanned, scanRequired, reasons, specifications, reseller, number = collo.index + 1}) {
    // The list is rendered in a different order than colli, so look the collo up by reference
    const colloIndex = colli.indexOf(collo)

    const options = type === 'pickup' ?
        pickupColloOptions({collo, scanRequired, scanned, reasons, specifications}) :
        deliveryColloOptions({collo, scanRequired, scanned, reasons, specifications})

    if (collo.isNewCollo) {
        options.push({title: t('delete'), value: 'remove', subvalue: ''})
    }

    const option = await popup.openOptions(`${number}: ${collo.barcode || t('noBarcode')}${collo.type ? ` (${getColliTypeName(collo.type, reseller)})` : ''}`, '', options)

    // Closed without choosing
    if (!option) {
        return false
    }

    if (option.value === 'remove') {
        colli.splice(colloIndex, 1)
        return false
    }

    // For a pickup the reason is in the subvalue, for a delivery in the value
    const reason = type === 'pickup' ? option.subvalue : option.value

    let reasonSpecification = ''

    if (option.specifications) {
        const specificationOptions = []

        for (const specification of specifications.filter((specification) => specification.reason === reason)) {
            specificationOptions.push({title: specification.specification, value: option.value, subvalue: specification.specification, selected: collo.reasonSpecification === specification.specification})
        }

        const specificationOption = await popup.openOptions(`${type === 'pickup' ? dbCodes.colloReasons[reason] : dbCodes.colloStatus[reason]}`, '', specificationOptions)

        // Closed without choosing a specification, leave the collo and scanned as they were
        if (!specificationOption) {
            return false
        }

        reasonSpecification = specificationOption.subvalue
    }

    if ((type === 'pickup' && reason === 'notready') || (type === 'delivery' && reason === 'missing')) {
        const index = scanned.findIndex((barcode) => collo.barcode === barcode)
        if (index > -1) {
            scanned.splice(index, 1)
        }
    }

    collo.reasonSpecification = reasonSpecification
    collo.status = option.value
    collo.reason = option.subvalue

    return true
}

// Statuses an emballage item can get at a delivery address
export function deliveryPackagingOptions(packaging) {
    if (packaging.initialStatus === 'collected' || packaging.initialStatus === 'athub') {
        return [
            {title: dbCodes.packagingStatus['delivered'], value: 'delivered', selected: packaging.status === 'delivered'},
            {title: dbCodes.packagingStatus['return_collected'], value: 'return_collected', selected: packaging.status === 'return_collected'},
            {title: dbCodes.packagingStatus['manco'], value: 'manco', selected: packaging.status === 'manco'}
        ]
    }

    if (packaging.initialStatus === 'delivered') {
        return [
            {title: dbCodes.packagingStatus['collected'], value: 'collected', selected: packaging.status === 'collected'},
            {title: dbCodes.packagingStatus['notcollected'], value: 'notcollected', selected: packaging.status === 'notcollected'},
            {title: dbCodes.packagingStatus['manco'], value: 'manco', selected: packaging.status === 'manco'}
        ]
    }

    if (packaging.initialStatus === 'return_collected' || packaging.initialStatus === 'return_athub') {
        return [
            {title: dbCodes.packagingStatus['delivered'], value: 'delivered', selected: packaging.status === 'delivered'},
            {title: dbCodes.packagingStatus['manco'], value: 'manco', selected: packaging.status === 'manco'}
        ]
    }

    return []
}

// Statuses an emballage item can get at a pickup address
export function pickupPackagingOptions(packaging) {
    let options = []

    if (packaging.status === 'registered') {
        options = [
            {title: dbCodes.packagingStatus['collected'], value: 'collected'},
            {title: dbCodes.packagingStatus['cancelled'], value: 'cancelled'}
        ]
    }

    if (packaging.initialStatus === 'delivered') {
        options = [
            {title: dbCodes.packagingStatus['collected'], value: 'collected', selected: packaging.status === 'collected'},
            {title: dbCodes.packagingStatus['notcollected'], value: 'notcollected', selected: packaging.status === 'notcollected'},
            {title: dbCodes.packagingStatus['manco'], value: 'manco', selected: packaging.status === 'manco'}
        ]
    }

    if (packaging.initialStatus === 'return_collected' || packaging.initialStatus === 'return_athub') {
        options = [
            {title: dbCodes.packagingStatus['delivered'], value: 'delivered', selected: packaging.status === 'delivered'},
            {title: dbCodes.packagingStatus['manco'], value: 'manco', selected: packaging.status === 'manco'}
        ]
    }

    return options
}

// Lets the messenger choose the status of an emballage item, updates it in place
export async function choosePackagingStatus(popup, type, packaging) {
    const options = type === 'pickup' ? pickupPackagingOptions(packaging) : deliveryPackagingOptions(packaging)

    const option = await popup.openOptions(`${packaging.type || ''}: ${packaging.barcode || t('noBarcode')}`, '', options)

    // Closed without choosing
    if (!option) {
        return
    }

    packaging.status = option.value
}
